import { defineStoredJson } from '../utils/storage';

// ---------------------------------------------------------------------------
/**
 * One-off repairs: what a device can only get back by READING A TABLE AGAIN.
 *
 * A document left quietly wrong on a device cannot be fixed by shipping
 * corrected code — the stored copy is what is wrong, and nothing will send it
 * again: the server row is untouched so its `updatedAt` never moves, and RxDB's
 * downstream skips a row whose fork and assumed master agree. It stays frozen
 * and wrong for ever. "Daten neu laden" repairs that, but only if a person in
 * the field asks for it, so a repair is named here instead and runs once,
 * silently, on the collections it concerns.
 *
 * A repair is a fact about the past: leave entries in place once written. A
 * device away for a year updates straight to today's build and still owes
 * every repair it missed.
 */
// ---------------------------------------------------------------------------

export interface Repair {
    /** Stable id; recorded on the device once the walk has finished. */
    id: string;
    /** RxDB collection names to read again, in full. */
    collections: string[];
    /** Why — for whoever reads this list in a year. */
    reason: string;
}

export const REPAIRS: Repair[] = [
    {
        id: 'migration-defaults-office-tables-2026-09',
        /**
         * The small tables the OFFICE writes while everyone else's device is
         * running — losing a value this way needs the field set before that
         * device updated. The large field-written tables (Befunde, Fotos,
         * Zeichenblaetter, Tagebuecher, Zeiten) are deliberately left out:
         * there the device's own user fills the field in, and walking them on
         * forty tablets wants a reason, not a precaution.
         */
        collections: ['user_profiles', 'projects', 'groups', 'project_groups'],
        reason:
            'Bis 0.80.0 schrieb jede Migrationsstrategie ihren Vorgabewert ' +
            'ueber ein Feld, das ein Geraet vom Server schon erhalten hatte ' +
            '(siehe shared/src/migrations/shared.ts). Bei den Profilen traf das den ' +
            'Crewmeister-Namen und das Grabungsleiter-Kennzeichen: gesetzt im ' +
            'Buero, auf jedem laufenden Geraet beim naechsten Update wieder ' +
            'auf den Vorgabewert zurueckgesetzt — und danach unerreichbar. Am ' +
            'Projekt stehen dieselben Felder zur Debatte: Mannschaft, ' +
            'Kontingente, die Personen-Verknuepfungen.'
    },
    {
        id: 'user-profile-emails-2026-10',
        collections: ['user_profiles'],
        reason:
            'Die Firmen- und privaten E-Mail-Adressen (0.101.0) kamen per ' +
            'Skript direkt in die Datenbank, ohne Nachricht an die Geraete. ' +
            'Wer die Profile danach nicht vollstaendig einlas, zeigte die ' +
            'Spalten leer.'
    }
];

/**
 * Not `keepOnWipe`: a wipe throws the database away with it, and what comes
 * back is pulled fresh anyway.
 */
const completedRepairs = defineStoredJson<string[]>({
    key: 'completed_repairs',
    fallback: () => [],
    decode: (parsed) =>
        Array.isArray(parsed)
            ? parsed.filter((id): id is string => typeof id === 'string')
            : undefined
});

export function pendingRepairs(): Repair[] {
    const done = new Set(completedRepairs.read());
    return REPAIRS.filter((repair) => !done.has(repair.id));
}

function recordRepair(id: string): void {
    const done = completedRepairs.read();
    if (!done.includes(id)) {
        completedRepairs.write([...done, id]);
    }
}

/**
 * `walk` re-reads the named collections from the first row (see
 * `startCollectionWalk` in replication.ts); it is passed in so this list stays
 * a list. A repair that throws is NOT recorded — recording it optimistically
 * would spend the one chance the device had.
 */
export async function runPendingRepairs(
    walk: (collections: string[]) => Promise<void>
): Promise<void> {
    for (const repair of pendingRepairs()) {
        try {
            await walk(repair.collections);
            recordRepair(repair.id);
        } catch {
            // Offline, signed out, or interrupted — owed again next time.
            return;
        }
    }
}
